import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/GiantLog-logo.png";
import { getUiInterface } from "../../locales/translationUtils";
import {
  redefinirSenhaComToken,
  validarTokenRedefinicaoSenha,
} from "../../services/userService";

/**
 * Tela pública de troca de senha, acessada pelo link enviado por e-mail quando
 * o administrador reseta a senha de um usuário.
 */
const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [validando, setValidando] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmaNovaSenha, setConfirmaNovaSenha] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);

  useEffect(() => {
    const validaToken = async () => {
      if (!token) {
        setErro(t("resetPassword.invalidLink"));
        setValidando(false);
        return;
      }

      try {
        const resposta = await validarTokenRedefinicaoSenha(token);
        setNomeUsuario(resposta?.data?.fullName || resposta?.data?.username || "");
        setTokenValido(true);
      } catch (err: any) {
        const messages: string[] | undefined = err?.response?.data?.messages;
        setErro(messages?.length ? messages.join(" ") : t("resetPassword.invalidLink"));
      } finally {
        setValidando(false);
      }
    };

    validaToken();
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);

    if (!novaSenha || !confirmaNovaSenha) {
      setErro(t("passwordError.required"));
      return;
    }

    if (novaSenha !== confirmaNovaSenha) {
      setErro(t("passwordError.match"));
      return;
    }

    if (novaSenha.length < 6) {
      setErro(t("passwordError.length"));
      return;
    }

    setSalvando(true);

    try {
      const resposta = await redefinirSenhaComToken({
        token,
        novaSenha,
        confirmaNovaSenha,
      });

      setSucesso(
        resposta?.messages?.length
          ? resposta.messages.join(" ")
          : t("resetPassword.success")
      );
      setTokenValido(false);
    } catch (err: any) {
      const messages: string[] | undefined = err?.response?.data?.messages;
      setErro(messages?.length ? messages.join(" ") : t("passwordError.update"));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal show={true} backdrop="static" keyboard={false} centered>
      <Modal.Header className="d-flex justify-content-center align-items-center">
        <img src={logo} alt="Logo" style={{ width: "150px", height: "auto" }} />
      </Modal.Header>
      <Modal.Body>
        <h5 className="mb-3">{t("resetPassword.title")}</h5>

        {validando && (
          <div className="text-center my-3">
            <Spinner animation="border" role="status" size="sm" className="me-2" />
            {t("resetPassword.validating")}
          </div>
        )}

        {!validando && sucesso && (
          <div className="alert alert-success" role="alert">
            {sucesso}
          </div>
        )}

        {!validando && !sucesso && erro && !tokenValido && (
          <div className="alert alert-danger" role="alert">
            {erro}
          </div>
        )}

        {!validando && tokenValido && (
          <Form onSubmit={handleSubmit}>
            {nomeUsuario && (
              <p className="text-muted" style={{ fontSize: "14px" }}>
                {t("resetPassword.greeting", { user: nomeUsuario })}
              </p>
            )}

            <Form.Group controlId="formNovaSenha">
              <Form.Label>{t("resetPassword.newPassword")}</Form.Label>
              <Form.Control
                type="password"
                value={novaSenha}
                autoFocus
                onChange={(e) => setNovaSenha(e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="formConfirmaNovaSenha" className="mt-3">
              <Form.Label>{t("resetPassword.confirmPassword")}</Form.Label>
              <Form.Control
                type="password"
                value={confirmaNovaSenha}
                onChange={(e) => setConfirmaNovaSenha(e.target.value)}
              />
            </Form.Group>

            {erro && <div className="text-danger mt-2">{erro}</div>}

            <div className="d-grid mt-4">
              <Button type="submit" variant="primary" disabled={salvando}>
                {salvando ? t("user.labels.saving") : getUiInterface("save")}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => navigate("/login")}>
          {t("resetPassword.backToLogin")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ResetPassword;
